<script lang="ts">
  import Fooji from "@fooji/microsites";
  import { onDestroy, onMount } from "svelte";
  import { verifyStore } from "../lib/stores/verify";
  import { registrationStore } from "../lib/stores/registration";
  import { saveJwt } from "../lib/helpers/auth";
  import Modal from "./Modal.svelte";
  import { isDemo } from "../lib/stores/mode";
  import { copy } from "../lib/stores/copy";

  let verificationError: string = "";

  const onClose = () => {
    $verifyStore.verificationData = null;
    $verifyStore.onVerifyCallback = () => {};
  }

  let countdown = 0;
  let countdownInterval: number;

  const stopCountdown = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
  };

  onDestroy(() => stopCountdown());
  onMount(() => startCountdown());

  let codeInputValues: string[] = [];
  let codeInputElements: HTMLInputElement[] = [];

  const startCountdown = () => {
    stopCountdown();

    const updateCountdown = () => {
      if (!$verifyStore.verificationData) return;
      const timeLeft = Math.ceil((new Date($verifyStore.verificationData.nextTryAt).getTime() - Date.now()) / 1000);
      countdown = timeLeft > 0 ? timeLeft : 0;
    };

    updateCountdown();
    countdownInterval = setInterval(updateCountdown, 1000) as unknown as number;
  }

  const handlePaste = (event: ClipboardEvent) => {
    const pasted = event.clipboardData?.getData("text") || "";
    setCodeInputValue(pasted);

    event.preventDefault();
  };

  const setCodeInputValue = (value: string) => {
    const digits = value.replace(/\D/g, "").split("").slice(0, 6);

    digits.forEach((digit, i) => {
      if (codeInputElements[i]) {
        codeInputElements[i].value = digit;
        codeInputValues[i] = digit;
      }
    });

    const next = digits.length < 6 ? codeInputElements[digits.length] : null;
    if (next) next.focus();
  };

  function handleInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;

    // Handle autofill from SMS - if the input value is longer than 1 character, it's likely a full code
    if (index === 0 && input.value.length > 1) {
      setCodeInputValue(input.value);
    } else {
      input.value = input.value.replace(/\D/g, "").slice(0, 1);
      codeInputValues[index] = input.value;

      if (input.value && codeInputElements[index + 1]) {
        codeInputElements[index + 1]?.focus();
      }
    }
  }

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    const input = event.target as HTMLInputElement;

    if (event.key === "Backspace") {
      if (!input.value && index > 0) {
        const prevInput = codeInputElements[index - 1];
        if (prevInput) {
          prevInput.focus();
          prevInput.value = "";
          codeInputValues[index - 1] = "";
          event.preventDefault();
        }
      }
    }
  };

  const handleChange = (event: Event, index: number) => {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, "").slice(0, 1);
    codeInputValues[index] = value;
  };

  const setDemoVerificationData = (channel: "sms" | "email") => {
    $verifyStore.verificationData = {
      channelUsed: channel,
      nextTryAt: new Date(Date.now() + 1000 * 60).toISOString(),
      last4PhoneNumber: "1234"
    };
  }

  const requestNewVerificationCode = async (channel: "sms" | "email", errorMessage: string = $copy.verifyModal.errors.requestFailed) => {
    if (isDemo()) {
      return setDemoVerificationData(channel);
    }

    const updateIdentityResponse = await Fooji.Auth.verify.updateIdentity({
      email: $registrationStore.email,
      phone: "",
      channel
    });

    if (updateIdentityResponse.status === "needs-verification") {
      if (!$verifyStore.verificationData) return;
      $verifyStore.verificationData = updateIdentityResponse.data;
    } else {
      verificationError = errorMessage;
    }
  };

  const onToggleChannel = async () => {
    if (!$verifyStore.verificationData) return;

    const newChannel = $verifyStore.verificationData.channelUsed === "sms" ? "email" : "sms";

    if (isDemo()) {
      return setDemoVerificationData(newChannel);
    } else {
      await requestNewVerificationCode(newChannel, $copy.verifyModal.errors.toggleChannelFailed);
    }
  }

  const completeVerification = async () => {
    verificationError = '';

    try {
      // ignoring the user output as we're going to save over it...
      if (!isDemo()) {
        const { jwt } = await Fooji.Auth.verify.mergeAccount($registrationStore.email, codeInputValues.join(""));
        saveJwt(jwt);
      }

      $verifyStore.onVerifyCallback();
      $verifyStore.verificationData = null;
    } catch (error) {
      if (error instanceof Error && error.message.includes("You cannot merge two users that both have the callofduty identity")) {
        verificationError = $copy.verifyModal.errors.multipleCallOfDutyIdentities;
        return;
      }

      verificationError = $copy.verifyModal.errors.verificationFailed;

      if (error instanceof Error && !error.message.includes("Invalid code")) {
        Fooji.Bugsnag.notify({
          errorMessage: $copy.verifyModal.errors.verificationFinishFailed.replace('{errorMessage}', error.message),
          error: error
        });
      }
    }
  };

  $: hasPhone = $registrationStore.phone || isDemo();
</script>

<Modal 
  isOpen={!!$verifyStore.verificationData}
  onClose={onClose}
  closeButtonAriaLabel={$copy.verifyModal.closeButtonAriaLabel}
  modalAriaLabel="verification-title"
  modalAriaDescription={$copy.verifyModal.modalAriaDescription}
>
  <div class="bg-primary-orange relative">
      <div class="space-y-3 text-center">
        <h2 id="verification-title" class="font-MrEavesXLSanNarOTUltra text-primary text-2xl lg:text-3xl font-black uppercase">
          {$copy.verifyModal.title}
        </h2>
        
        <p id="verification-message" class="font-MrEavesXLSanNarOT text-white text-base lg:text-xl leading-tight mt-2">
          {#if $verifyStore.verificationData?.channelUsed === "sms"}
            {$copy.verifyModal.smsMessage.replace('{phoneNumber}', $verifyStore.verificationData.last4PhoneNumber)}
          {:else}
            {$copy.verifyModal.emailMessage}
          {/if}
        </p>
        
        {#if hasPhone}
          <p class="font-MrEavesXLSanNarOT text-white text-base lg:text-xl leading-tight mt-2">
            {#if $verifyStore.verificationData?.channelUsed === "sms"}
              {$copy.verifyModal.toggleToEmail}
            {:else}
              {$copy.verifyModal.toggleToSms}
            {/if}
            <a
              href="/"
              on:click={(e) => {
                e.preventDefault();
                $verifyStore.verificationData && onToggleChannel();
              }}
              class="underline transition-all duration-300 ease-in-out hover:opacity-80"
            >
              {$copy.verifyModal.toggleLink}
            </a>.
          </p>
        {/if}
      </div>
      <div class="space-y-5 mt-5">
        <div class="flex justify-center items-center gap-1 mobile:gap-2.5 lg:gap-5" on:paste={handlePaste}>
          {#each Array(6) as _, i}
            <!-- svelte-ignore a11y-autofocus -->
            <input
              bind:this={codeInputElements[i]}
              bind:value={codeInputValues[i]}
              type="text"
              minlength="1"
              maxlength={i === 0 ? 6 : 1}
              inputmode="numeric"
              autofocus={i === 0}
              autocomplete={i === 0 ? "one-time-code" : "off"}
              class="flex items-center justify-center h-12 lg:h-14 w-10 lg:w-16 bg-grey focus:outline-none px-2 lg:px-4 text-center text-input text-sm lg:text-lg border border-transparent rounded"
              on:change={(e) => handleChange(e, i)}
              on:input={(e) => handleInput(e, i)}
              on:keydown={(e) => handleKeyDown(e, i)}
            />
          {/each}
        </div>
        <div class="flex justify-center">
          <button
            type="button"
            class="flex justify-center items-center w-auto text-base lg:text-xl text-white font-medium uppercase bg-primary h-10 lg:h-12 px-8 lg:px-12 transition-all duration-300 ease-in-out hover:bg-primary/80 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            on:click={() => completeVerification()}
            disabled={codeInputValues.length !== 6}
          >
            {$copy.verifyModal.verifyButton}
          </button>
        </div>
        <button
          class="text-white text-sm lg:text-2xl"
          class:underline={countdown === 0}
          disabled={countdown > 0}
          on:click={() => $verifyStore.verificationData && requestNewVerificationCode($verifyStore.verificationData.channelUsed)}
        >
          {
            countdown > 0
              ? $copy.verifyModal.countdownMessage.replace('{countdown}', countdown.toString())
              : $copy.verifyModal.requestNewCode
          }
        </button>
        {#if verificationError}
          <p class="text-red-100 text-sm lg:text-2xl text-center">
            {@html verificationError}
          </p>
        {/if}
      </div>
  </div>
</Modal>
